import { useCallback, useEffect, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faMagnifyingGlass,
  faRotateRight,
  faEye,
  faArrowRotateLeft,
} from '@fortawesome/free-solid-svg-icons';
import { cn } from '@/lib/cn';
import { toast, swalConfirm } from '@/lib/swal';
import { listAssetsPage, restoreAsset } from './api/ame-platform.api';
import type { AssetRecord, AssetListParams } from './api/ame-platform.api';
import { AssetStatusBadge } from './components/AssetStatusBadge';
import { FileSizeDisplay } from './components/FileSizeDisplay';
import { AssetMetadataModal } from './components/AssetMetadataModal';

const LIMIT = 20;

const MODULE_OPTIONS = [
  'inspection',
  'procedure',
  'equipment',
  'company-profile',
  'user',
  'knowledge-base',
];
const CLASS_OPTIONS = [
  'photo',
  'video',
  'document',
  'thumbnail',
  'logo',
  'favicon',
  'profile-photo',
  'qr-branded',
];
const STATUS_OPTIONS = ['ACTIVE', 'SOFT_DELETED', 'QUARANTINED', 'PERMANENTLY_DELETED'];

function Skeleton() {
  return (
    <tr>
      {Array.from({ length: 8 }).map((_, i) => (
        <td key={i} className="px-4 py-3">
          <div className="h-4 rounded bg-muted animate-pulse" />
        </td>
      ))}
    </tr>
  );
}

export function AssetExplorerPage() {
  const [assets, setAssets] = useState<AssetRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [cursor, setCursor] = useState<string | undefined>(undefined);
  const [selectedAsset, setSelectedAsset] = useState<AssetRecord | null>(null);

  const [filters, setFilters] = useState<Omit<AssetListParams, 'limit' | 'cursor'>>({});
  const [search, setSearch] = useState('');
  const searchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const fetchAssets = useCallback(async (params: AssetListParams, append: boolean) => {
    try {
      // The SERVER decides where the next page starts. Deriving the cursor on
      // the client duplicated that rule and would silently drift if the backend
      // ever changed it.
      const { assets: results, nextCursor } = await listAssetsPage({ ...params, limit: LIMIT });
      if (append) {
        setAssets((prev) => [...prev, ...results]);
      } else {
        setAssets(results);
      }
      setCursor(nextCursor ?? undefined);
      setHasMore(nextCursor !== null);
    } catch {
      toast.error('Failed to load assets');
    }
  }, []);

  const reload = useCallback(
    (newFilters: Omit<AssetListParams, 'limit' | 'cursor'>) => {
      setLoading(true);
      setCursor(undefined);
      setHasMore(false);
      fetchAssets({ ...newFilters }, false).finally(() => setLoading(false));
    },
    [fetchAssets],
  );

  useEffect(() => {
    reload(filters);
  }, [filters, reload]);

  const handleSearchChange = (value: string) => {
    setSearch(value);
    if (searchTimer.current) clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => {
      setFilters((prev) => ({ ...prev, search: value || undefined }));
    }, 400);
  };

  const handleFilterChange = (key: keyof typeof filters, value: string) => {
    setFilters((prev) => ({ ...prev, [key]: value || undefined }));
  };

  const handleLoadMore = async () => {
    if (!cursor || loadingMore) return;
    setLoadingMore(true);
    await fetchAssets({ ...filters, cursor }, true);
    setLoadingMore(false);
  };

  const handleRestore = async (asset: AssetRecord) => {
    const confirmed = await swalConfirm({
      title: 'Restore Asset',
      text: `Restore "${asset.originalFileName}" to ACTIVE status?`,
      confirmText: 'Restore',
    });
    if (!confirmed) return;
    try {
      await restoreAsset(asset.id);
      toast.success('Asset restored successfully');
      reload(filters);
    } catch {
      toast.error('Failed to restore asset');
    }
  };

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-xl font-semibold text-foreground">Asset Explorer</h1>
        <p className="mt-0.5 text-sm text-muted-foreground">
          Browse and filter all assets across tenants
        </p>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative">
          <FontAwesomeIcon
            icon={faMagnifyingGlass}
            className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground"
          />
          <input
            type="text"
            value={search}
            onChange={(e) => handleSearchChange(e.target.value)}
            placeholder="Search filename or key…"
            className="h-9 w-64 rounded-lg border border-border bg-background pl-9 pr-3 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
          />
        </div>

        <select
          value={filters.moduleId ?? ''}
          onChange={(e) => handleFilterChange('moduleId', e.target.value)}
          className="h-9 rounded-lg border border-border bg-background px-3 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
        >
          <option value="">All Modules</option>
          {MODULE_OPTIONS.map((m) => (
            <option key={m} value={m}>
              {m}
            </option>
          ))}
        </select>

        <select
          value={filters.assetClass ?? ''}
          onChange={(e) => handleFilterChange('assetClass', e.target.value)}
          className="h-9 rounded-lg border border-border bg-background px-3 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
        >
          <option value="">All Classes</option>
          {CLASS_OPTIONS.map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </select>

        <select
          value={filters.status ?? ''}
          onChange={(e) => handleFilterChange('status', e.target.value)}
          className="h-9 rounded-lg border border-border bg-background px-3 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
        >
          <option value="">All Statuses</option>
          {STATUS_OPTIONS.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>

        <button
          onClick={() => {
            setSearch('');
            setFilters({});
          }}
          className="flex h-9 items-center gap-1.5 rounded-lg border border-border bg-background px-3 text-sm text-muted-foreground hover:text-foreground transition-colors"
        >
          <FontAwesomeIcon icon={faRotateRight} className="h-3.5 w-3.5" />
          Reset
        </button>
      </div>

      {/* Table */}
      <div className="overflow-x-auto rounded-xl border border-border bg-card">
        <table className="min-w-full divide-y divide-border text-sm">
          <thead>
            <tr className="bg-muted/40">
              <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground">
                File Name
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground">
                Module
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground">
                Class
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground">
                Size
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground">
                Status
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground">
                Version
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground">
                Created
              </th>
              <th className="px-4 py-3 text-right text-xs font-semibold text-muted-foreground">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {loading ? (
              Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} />)
            ) : assets.length === 0 ? (
              <tr>
                <td colSpan={8} className="py-16 text-center text-sm text-muted-foreground">
                  No assets found
                </td>
              </tr>
            ) : (
              assets.map((asset) => (
                <tr key={asset.id} className="hover:bg-muted/30 transition-colors">
                  <td className="max-w-[200px] truncate px-4 py-3 text-sm font-medium text-foreground">
                    {asset.originalFileName}
                  </td>
                  <td className="px-4 py-3 text-sm text-muted-foreground">{asset.moduleId}</td>
                  <td className="px-4 py-3 text-sm text-muted-foreground">{asset.assetClass}</td>
                  <td className="px-4 py-3 text-sm text-muted-foreground">
                    <FileSizeDisplay bytes={asset.fileSizeBytes} />
                  </td>
                  <td className="px-4 py-3">
                    <AssetStatusBadge status={asset.assetStatus} />
                  </td>
                  <td className="px-4 py-3 text-sm text-muted-foreground">
                    v{asset.currentVersionNumber}
                  </td>
                  <td className="px-4 py-3 text-sm text-muted-foreground">
                    {new Date(asset.createdAt).toLocaleDateString()}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        onClick={() => setSelectedAsset(asset)}
                        className="flex h-7 w-7 items-center justify-center rounded-lg text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
                        title="View details"
                      >
                        <FontAwesomeIcon icon={faEye} className="h-3.5 w-3.5" />
                      </button>
                      {(asset.assetStatus === 'SOFT_DELETED' ||
                        asset.assetStatus === 'QUARANTINED') && (
                        <button
                          onClick={() => void handleRestore(asset)}
                          className="flex h-7 w-7 items-center justify-center rounded-lg text-muted-foreground hover:bg-green-500/10 hover:text-green-600 transition-colors"
                          title="Restore asset"
                        >
                          <FontAwesomeIcon icon={faArrowRotateLeft} className="h-3.5 w-3.5" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {hasMore && (
        <div className="flex justify-center">
          <button
            onClick={() => void handleLoadMore()}
            disabled={loadingMore}
            className={cn(
              'rounded-lg border border-border bg-card px-6 py-2 text-sm font-medium text-foreground transition-colors',
              loadingMore ? 'opacity-50 cursor-not-allowed' : 'hover:bg-muted',
            )}
          >
            {loadingMore ? 'Loading…' : 'Load More'}
          </button>
        </div>
      )}

      {selectedAsset && (
        <AssetMetadataModal asset={selectedAsset} onClose={() => setSelectedAsset(null)} />
      )}
    </div>
  );
}
